import {
    useState,
    useMemo,
    useEffect,
    useCallback,
    FormEvent,
    ChangeEvent,
} from 'react';
import {
    EditUserCollectionMutationVariables,
    EditUserCollectionMutation,
    UserCollection,
    CreateUserCollectionMutation,
    CreateUserCollectionMutationVariables,
    AddSamplesToUserCollectionMutation,
    AddSamplesToUserCollectionMutationVariables,
} from 'types/generated/graphql';
import { t } from '@lingui/macro';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { useMutation } from '@apollo/client';
import {
    CreateUserCollection,
    EditUserCollection,
    AddSamplesToUserCollection,
} from 'apollo/userCollection/userCollection.gql';
import { CloseModalType } from 'contexts/ModalContext/ModalContext.type';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { evictCacheByKeys } from 'helpers';
import { QueryConfig } from 'apollo/userCollection/queryConfig';
import {
    CollectionCreatedError,
    CollectionCreatedSuccess,
    CollectionRenamedError,
    CollectionRenamedSuccess,
} from 'contexts/NotificationsContext/notifications';

function getName(
    collection: UserCollection | null,
    placeholder: string | null | undefined,
): string {
    if (collection && collection.name) {
        return collection.name;
    }

    if (placeholder) {
        return placeholder;
    }

    return '';
}

interface CollectionModalEffectsProps {
    collection: UserCollection | null;
    isOpen: boolean;
    placeholder?: string | null;
    handleClose: CloseModalType;
    sampleIds?: Array<string>;
}

interface CollectionModalEffectsReturnValues {
    value: string;
    error: string | undefined;
    isDisabled: boolean;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleSubmit: (e: FormEvent) => void;
    handleClick: () => void;
    loading: boolean;
}

export const useCollectionModalEffects = ({
    collection,
    isOpen,
    placeholder,
    handleClose,
    sampleIds,
}: CollectionModalEffectsProps): CollectionModalEffectsReturnValues => {
    const [createdCollectionId, setCreatedCollectionId] = useState<
        string | undefined
    >(undefined);

    const [value, setValue] = useState(getName(collection, placeholder));
    const [isLoading, setIsLoading] = useState(false);
    const { clearSamplesSelection } = useSelectedSamplesContext();

    const isIncorrectLength = value.length > 255;

    const isDisabled = isIncorrectLength || !value.length;

    const error = useMemo(() => {
        const lengthError = isIncorrectLength
            ? t`256 characters maximum`
            : undefined;
        const duplicatedNameError = undefined;

        return lengthError || duplicatedNameError;
    }, [isIncorrectLength]);

    const handleCloseModal = useCallback(() => {
        handleClose(() => {
            // Timeout duration was taken from the maestro constant to fit current modal fade duration.
            // LANDR.Maestro/packages/maestro/src/components/Modal/Modal.tsx - TRANSITION_DURATION = 300
            setTimeout(() => setIsLoading(false), 300);
        });
    }, [handleClose, setIsLoading]);

    const { createNotification } = useNotificationsContext();

    const [
        requestCreateCollection,
        { loading: requestCreateCollectionLoading },
    ] = useMutation<
        CreateUserCollectionMutation,
        CreateUserCollectionMutationVariables
    >(CreateUserCollection, {
        variables: { name: value, sampleCount: sampleIds?.length ?? 0 },
        update: (cache) => {
            evictCacheByKeys(cache, [
                QueryConfig.pathToItems,
                'userCollectionsFeed',
            ]);
        },
        onCompleted: (data) => {
            setCreatedCollectionId(data?.createUserCollection?.guid);

            if (sampleIds?.length === 0) {
                createNotification(new CollectionCreatedSuccess(value));
                handleCloseModal();
            }
        },
        onError: () => {
            handleCloseModal();
            createNotification(new CollectionCreatedError(value));
        },
    });

    const [
        requestAddSamplesToUserCollection,
        { loading: requestAddSamplesToUserCollectionLoading },
    ] = useMutation<
        AddSamplesToUserCollectionMutation,
        AddSamplesToUserCollectionMutationVariables
    >(AddSamplesToUserCollection, {
        variables: { collectionGuid: createdCollectionId, sampleIds },
        update: (cache) => {
            evictCacheByKeys(cache, [
                collection?.guid,
                QueryConfig.pathToItems,
                'userCollectionsFeed',
            ]);
        },
        onCompleted: () => {
            handleCloseModal();
            createNotification(new CollectionCreatedSuccess(value));
        },
        onError: () => {
            handleCloseModal();
            createNotification(new CollectionCreatedError(value));
        },
    });

    useEffect(() => {
        if (createdCollectionId && Number(sampleIds?.length) > 0 && isOpen) {
            requestAddSamplesToUserCollection();
            clearSamplesSelection();
        }
    }, [
        createdCollectionId,
        sampleIds,
        isOpen,
        requestAddSamplesToUserCollection,
        clearSamplesSelection,
    ]);

    const [requestEditCollection, { loading: requestEditCollectionLoading }] =
        useMutation<
            EditUserCollectionMutation,
            EditUserCollectionMutationVariables
        >(EditUserCollection, {
            variables: { name: value, collectionGuid: collection?.guid },
            onCompleted: () => {
                handleCloseModal();
                createNotification(new CollectionRenamedSuccess(value));
            },
            update: (cache) => {
                evictCacheByKeys(cache, [
                    collection?.guid,
                    QueryConfig.pathToItems,
                    'userCollectionsFeed',
                ]);
            },
            onError: () => {
                handleCloseModal();
                createNotification(new CollectionRenamedError(value));
            },
        });

    useEffect(() => {
        if (isOpen) {
            setValue(getName(collection, placeholder));
        }
    }, [collection, isOpen, placeholder]);

    useEffect(() => {
        if (
            requestCreateCollectionLoading ||
            requestAddSamplesToUserCollectionLoading ||
            requestEditCollectionLoading
        ) {
            setIsLoading(true);
        }
    }, [
        requestCreateCollectionLoading,
        requestAddSamplesToUserCollectionLoading,
        requestEditCollectionLoading,
        setIsLoading,
    ]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
        },
        [setValue],
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (isDisabled || isLoading) {
                return;
            }

            // The submit prop takes different parameters depending on wether we are in the edit modal
            // or whether we are in the create collection modal
            collection
                ? requestEditCollection({
                      variables: {
                          name: value,
                          collectionGuid: collection.guid,
                      },
                  })
                : requestCreateCollection();
        },
        [
            isDisabled,
            collection,
            requestEditCollection,
            value,
            requestCreateCollection,
            isLoading,
        ],
    );

    const handleClick = useCallback(() => {
        if (isLoading) {
            return;
        }
        // The submit prop takes different parameters depending on wether we are in the edit modal
        // or whether we are in the create collection modal
        collection
            ? requestEditCollection({
                  variables: {
                      name: value,
                      collectionGuid: collection.guid,
                  },
              })
            : requestCreateCollection();
    }, [
        collection,
        requestEditCollection,
        value,
        requestCreateCollection,
        isLoading,
    ]);

    return {
        value,
        error,
        isDisabled,
        handleChange,
        handleClick,
        handleSubmit,
        loading: isLoading,
    };
};
